import { ShiftSlot } from '../../../shared/types/shiftTypes.ts';
import { pageDisplayOptions } from '../../types/pageDispalyOptions.ts';
import { OeeComponent, OeeComponents, OeeGraphValueTypes } from '../../utils/ooeGraphValueType.ts';
import { LiveGraphSettings, timeFrameOptions } from './liveGraphSettings.ts';


interface Props {
    style?: React.CSSProperties;
    className?: string;
    settings: LiveGraphSettings;
    setSettings: (liveGraphSettings: LiveGraphSettings) => void;
}

export function LiveGraphControls({
    style,
    className,
    settings,
    setSettings,
}: Props) {

    const handleShiftSelectionChange = (shiftSlot: ShiftSlot, isEnabled: boolean) => {
        const visibleShifts = isEnabled
            ? [...settings.visibleShifts, shiftSlot]
            : settings.visibleShifts.filter(s => s !== shiftSlot);

        setSettings({ ...settings, visibleShifts });
    }

    const handleComponentSelectionChange = (oeeComponent: OeeComponent, isEnabled: boolean) => {
        const oeeComponents = isEnabled
            ? [...settings.oeeComponents, oeeComponent]
            : settings.oeeComponents.filter(it => it !== oeeComponent);

        setSettings({ ...settings, oeeComponents });
    }

    const handleTimeFrameChange = (timeFrameKey: number) => {
        const timeFrame = timeFrameOptions.find(option => option.key === timeFrameKey);
        if (!timeFrame) return;
        setSettings({
            ...settings,
            timeFrame,
        });
    }

    const handleDisplayChange = (displayKey: string) => {
        const display = pageDisplayOptions.find(option => option.key === displayKey);
        if (!display) return;
        setSettings({
            ...settings,
            pageDisplay: display,
        });
    }


    const timeFrameKey = timeFrameOptions.find(it =>
        settings.timeFrame.days === it.days && settings.timeFrame.hours === it.hours
    )?.key ?? 1;

    return (
        <div
            className={className}
            style={{
                ...style,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                width: '100%',
                flexWrap: 'nowrap',
                overflowX: 'hidden',
                whiteSpace: 'nowrap',
            }}
        >
            <div className='live-graphs-controls__group live-graphs-controls__group--toggles'>
                <label>
                    <input
                        style={{ marginRight: '8px' }}
                        type='checkbox'
                        checked={settings.visibleShifts.includes('morning')}
                        onChange={(e) => handleShiftSelectionChange('morning', e.target.checked)}
                    />
                    Morning
                </label>
                <label>
                    <input
                        style={{ marginRight: '8px' }}
                        type='checkbox'
                        checked={settings.visibleShifts.includes('evening')}
                        onChange={(e) => handleShiftSelectionChange('evening', e.target.checked)}
                    />
                    Evening
                </label>
                <label>
                    <input
                        style={{ marginRight: '8px' }}
                        type='checkbox'
                        checked={settings.visibleShifts.includes('night')}
                        onChange={(e) => handleShiftSelectionChange('night', e.target.checked)}
                    />
                    Night
                </label>
                <label>
                    <input
                        style={{ marginRight: '8px' }}
                        type='checkbox'
                        checked={!settings.excludeWeekends}
                        onChange={(e) => setSettings({ ...settings, excludeWeekends: !e.target.checked })}
                    />
                    Weekends
                </label>
            </div>

            <div className='flex--horz live-graphs-controls__group'>
                <label htmlFor='timeFrame'>Time</label>
                <select
                    className='live-graphs-controls__select'
                    id='timeFrame'
                    value={timeFrameKey}
                    onChange={(e) => handleTimeFrameChange(+e.target.value)}
                >
                    {timeFrameOptions.map((option) => (
                        <option key={option.key} value={option.key}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className='flex--horz live-graphs-controls__group'>
                <label htmlFor='smoothingAmount'>Smoothing</label>
                <select
                    className='live-graphs-controls__select'
                    id='smoothingAmount'
                    value={settings.smoothingAmount}
                    onChange={(e) => setSettings({ ...settings, smoothingAmount: +e.target.value })}
                >
                    <option value={0}>None</option>
                    <option value={1}>1 bar</option>
                    <option value={2}>2 bars</option>
                </select>
            </div>

            <div className='flex--horz live-graphs-controls__group'>
                <label htmlFor='resolution'>Resolution</label>
                <select
                    className='live-graphs-controls__select'
                    id='resolution'
                    value={settings.resolution}
                    onChange={(e) => setSettings({ ...settings, resolution: e.target.value as any })}
                >
                    <option value='halfHour'>Half hour</option>
                    <option value='hour'>Hour</option>
                    <option value='shift'>Shift</option>
                </select>
            </div>

            <div className='flex--horz live-graphs-controls__group'>
                <label htmlFor='valueType'>Value type</label>
                <select
                    className='live-graphs-controls__select'
                    id='valueType'
                    value={settings.valueType}
                    onChange={(e) => setSettings({ ...settings, valueType: e.target.value as any })}
                >
                    {OeeGraphValueTypes.map((valueType) => (
                        <option key={valueType.value} value={valueType.value}>
                            {valueType.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className='flex--horz live-graphs-controls__group'>
                <label htmlFor='display'>Display</label>
                <select
                    className='live-graphs-controls__select'
                    id='display'
                    value={settings.pageDisplay?.key}
                    onChange={(e) => handleDisplayChange(e.target.value)}
                >
                    {pageDisplayOptions.map((option) => (
                        <option key={option.key} value={option.key}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            {settings.valueType === 'custom' &&
                OeeComponents.map((component) => (
                    <label key={component.value} className='live-graphs-controls__group' style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <input
                            style={{ marginRight: '8px' }}
                            type='checkbox'
                            checked={settings.oeeComponents.includes(component.value)}
                            onChange={(e) => handleComponentSelectionChange(component.value, e.target.checked)}
                        />
                        {component.name}
                    </label>
                ))}
        </div>
    );
}
